/**
 * WebSocket event names + namespace strings for shipment tracking.
 *
 * Used on both sides of the wire: backend gateways
 * (`backend-tms/src/tracking/gateways/dispatcher-tracking.gateway.ts`,
 * `backend-tms/src/customer-portal/gateways/portal-tracking.gateway.ts`)
 * declare the namespace and emit the events; frontend hooks + page
 * components subscribe via `useWebSocket({ namespace })`. Single source
 * of truth here means renames or new event types touch only one file.
 *
 * Tracked separately from EventEmitter event names in
 * `backend-tms/src/common/events/events.constants.ts` because the
 * EventEmitter side uses dot notation (`shipment.location.updated`,
 * `geofence.violation`) while the WS side uses colon notation
 * (`shipment:location-update`, `geofence:violation`) — established
 * convention.
 *
 * Namespace + event constants in this file are kept together because
 * dispatcher and portal namespaces share the same event vocabulary
 * (location/status/geofence-violation). Other unrelated WS namespaces
 * (`ai-routing`, `dashboard`, etc.) live in `ws-namespaces.ts`.
 */

export const TRACKING_WS_EVENTS = {
  LOCATION_UPDATE: 'shipment:location-update',
  STATUS_UPDATE: 'shipment:status-update',
  GEOFENCE_VIOLATION: 'geofence:violation',
  /**
   * Server → client error frame. Emitted by tracking gateways when a
   * handshake JWT check fails (#754) — mirrors the per-namespace ERROR
   * event convention already in place for chatbot and finance.
   */
  ERROR: 'tracking:error',
} as const;

export type TrackingWsEventName = (typeof TRACKING_WS_EVENTS)[keyof typeof TRACKING_WS_EVENTS];

export const TRACKING_WS_NAMESPACES = {
  DISPATCHER: 'dispatcher-tracking',
  PORTAL: 'portal-tracking',
} as const;

export type TrackingWsNamespace =
  (typeof TRACKING_WS_NAMESPACES)[keyof typeof TRACKING_WS_NAMESPACES];
